import { format } from "date-fns";

export const calculateRoundedAverage = (k45, l45, m45) => {
    const annualizedK = (k45 / 30) * 365;
    const annualizedL = (l45 / 90) * 365;
    const average = (annualizedK + annualizedL + m45) / 3;
    return Math.ceil(average);
}

export const calculateStockDays = (onHand, onOrder, averageDailySales) => {
  try {
    if (!averageDailySales || averageDailySales <= 0) {
      return 0;
    }
    const result = (onHand + onOrder) / averageDailySales;
    return result;
  } catch (error) {
    return 0;
  }
}

export const calculateWarehouseStockDays = (onHand, averageDailySales) => {
  try {
    if (!onHand || onHand === 0) {
      return 0;
    }
    if (!averageDailySales || averageDailySales <= 0) {
      return 0;
    }
    return onHand / averageDailySales;
  } catch (error) {
    return 0;
  }
}

export const calculateWarehouseSellOutDate = (onHand, daysOfStock, dailyAvg) => {
  if ((!onHand || onHand === 0) && (!daysOfStock || daysOfStock === 0)) {
    return "Sold Out";
  };
  if (!dailyAvg || dailyAvg <= 0) {
    return "No Velocity";
  };
  const maxDate = new Date("9999-12-31T00:00:00");
  try {
    const currentDate = new Date();
    currentDate.setDate(currentDate.getDate() + daysOfStock);
    if (currentDate > maxDate) {
      return format(new Date(maxDate.toISOString().split('T')[0]), 'MMM d, yyyy')
    }
    return format(new Date(currentDate.toISOString().split('T')[0]), 'MMM d, yyyy')
  } catch {
    return format(new Date(maxDate.toISOString().split('T')[0]), 'MMM d, yyyy');
  }
}

export const getReorderDeadline = (stockDays) => {
    if (!stockDays) return null;
    if (stockDays === 180 || (stockDays > 180 && stockDays < 181) || stockDays < 180) return getEstimatedDateByShippingMethod(new Date(), 0);
    if (stockDays >= 181) return getEstimatedDateByShippingMethod(new Date(), stockDays - 180)
    return null;
}

export const getEstimatedDateByShippingMethod = (date, addedDays) => {
  const maxDate = new Date(Date.UTC(9999, 11, 31, 0, 0, 0, 0));
  try {
    const daysToAdd = Number.isFinite(Number(addedDays)) ? Number(addedDays) : 0;
    const newDate = new Date(date);
    newDate.setUTCHours(0, 0, 0, 0);
    newDate.setUTCDate(newDate.getUTCDate() + daysToAdd);

    const clampedDate = newDate.getTime() > maxDate.getTime() ? maxDate : newDate;
    return format(clampedDate, "yyyy-MM-dd'T'HH:mm:ss");
  } catch {
    return format(maxDate, "yyyy-MM-dd'T'HH:mm:ss");
  }
}

export const calculateSellOutDate = (offset) => {
  const maxDate = new Date(Date.UTC(9999, 11, 31, 0, 0, 0, 0));
  try {
    const daysToAdd = Number.isFinite(Number(offset)) ? Number(offset) : 0;
    const currentDate = new Date();
    currentDate.setUTCHours(0, 0, 0, 0);
    currentDate.setUTCDate(currentDate.getUTCDate() + daysToAdd);

    const clampedDate = currentDate.getTime() > maxDate.getTime() ? maxDate : currentDate;
    return format(new Date(clampedDate.toISOString().split('T')[0]), 'MMM d, yyyy')
  } catch {
    return format(new Date(maxDate.toISOString().split('T')[0]), 'MMM d, yyyy');
  }
}

export const getInventoryHeaderTooltip = (column) => {
  if (column === "on_hand") {
    return "Sum of On Hand quantities from all specified warehouses";
  }
  if (column === "on_order") {
    return "Sum of (Quantity - Quantity Received)<br />Note: From all open purchase orders created before current time";
  }
  if (column === "sales_last_30") {
    return "Count of order line items in last 30 days (minus refunds in last 30 days)";
  }
  if (column === "sales_last_90") {
    return "Count of order line items in last 90 days (minus refunds in last 90 days)";
  }
  if (column === "sales_last_365") {
    return "Count of order line items in last 365 days (minus refunds in last 365 days)";
  }
  if (column === "sales_daily_avg") {
    return "Computed from trailing periods (30D/90D/365D): average of the non-zero daily rates (Sales ÷ Days).<br/>We have 3 periods (30D, 90D, 365D). If some period have 0 sales - we exclude it from the calculation.<br/>Example: Sales 30D = 100, Sales 90D = 0, Sales 365D = 100. We will calculate the average as (100 / 30) + (100 / 365) / 2.<br/>Note: If all periods have 0 sales, returns 0.";
  }
  if (column === "days_of_stock") {
    return "(On Hand + On Order + Extra Ordered) ÷ Daily Avg<br/>Note: Returns 0 if Daily Avg is 0 or negative.";
  }
  if (column === "sell_out_date") {
    return "Current Date + Total Days Of Stock<br/>Note: If Days Of Stock is 0, Sellout Date can be today.";
  }
  if (column === "reorder_deadline") {
    return "If Total Days Of Stock ≤ 180: Current Date<br/>If Total Days Of Stock ≥ 181: Current Date + (Total Days Of Stock - 180)<br/>Note: Returns N/A if Total Days Of Stock is 0.";
  }
  if (column === "reorder_quantity") {
    return "Sales 90D × 2<br/>Note: Returns 0 if Sales 90D is 0";
  }
  if (column === "sell_out_180_days") {
    return "Days Of Stock < 181<br/>Note: Returns ✕ if Days Of Stock < 181, ✔ if Days Of Stock ≥ 181";
  }
  if (column === "stock_arrives_on") {
    return "Shows PO number and created date for each open purchase order.<br/>If nothing is on order, this can show “Nothing On Order”.";
  }
  if (column === "extra_ordered") {
    return "User input field for additional order quantity<br/>Note: When entered, recalculates: Total Days Of Stock, Sellout Date, Reorder Deadline";
  }
  if (column === "warehouse_days_of_stock") {
    return "On Hand ÷ Daily Avg (excludes On Order)";
  }
  if (column === "warehouse_sell_out_date") {
    return "Current Date + Warehouse Days Of Stock (excludes On Order).<br/>May show “Sold Out” (no on hand) or “No Velocity” (no sales).";
  }
  return null;
}

export const getInventoryCalculationHeaderTooltip = (column) => {
  if (column === "on_hand") {
    return "Sum of On Hand quantities from all specified warehouses";
  }
  if (column === "on_order") {
    return "Sum of (Quantity - Quantity Received)<br />Note: From all open purchase orders created before current time";
  }
  if (column === "sales_last_30") {
    return "Count of order line items in last 30 days (minus refunds in last 30 days)";
  }
  if (column === "sales_last_90") {
    return "Count of order line items in last 90 days (minus refunds in last 90 days)";
  }
  if (column === "sales_last_365") {
    return "Count of order line items in last 365 days (minus refunds in last 365 days)";
  }
  if (column === "sales_daily_avg") {
    return "Depends on Demand Basis:<br/>We have 3 periods (30D, 90D, 365D). If some period have 0 sales - we exclude it from the calculation.<br/>• Trailing 30D: (Sales Last 30D ÷ 30)<br/>• Trailing 90D: ((Sales Last 30D ÷ 30), (Sales Last 90D ÷ 90)) / 2<br/>• Trailing 365D: ((Sales Last 30D ÷ 30), (Sales Last 90D ÷ 90), (Sales Last 365D ÷ 365)) / 3<br/>• Custom Range: sum (Amazon + Shopify) daily quantity_with_refunds from Start Date for N = floor((End - Start) / 1 day) days (end date excluded); Daily Avg = Total ÷ N (0 if N ≤ 0)<br/>• YoY Quarter: uses Year - 1; sums daily quantity_with_refunds for each day in the quarter; Daily Avg = Total ÷ daysDiff where daysDiff = floor((quarterEnd - quarterStart) / 1 day)<br/>Note: All results are rounded to 2 decimals; non-positive results become 0";
  }
  if (column === "days_of_stock") {
    return "(On Hand + On Order + Extra Ordered) ÷ Daily Avg<br/>Note: Uses growth Daily Avg if Growth Multiplier > 0<br/>Returns 0 if Daily Avg is 0 or negative<br/>Includes Extra Ordered quantity in calculation";
  }
  if (column === "sell_out_date") {
    return "Current Date + Total Days Of Stock<br/>Note: Uses growth Days Of Stock if Growth Multiplier > 0<br/>Only calculated if Days Of Stock > 0";
  }
  if (column === "reorder_deadline") {
    return "If Total Days Of Stock < 180: Current Date<br/>If Total Days Of Stock ≥ 181: Current Date + (Total Days Of Stock - 180)<br/>Note: Uses growth Days Of Stock if Growth Multiplier > 0<br/>Returns N/A if Total Days Of Stock is 0";
  }
  if (column === "reorder_quantity") {
    return "Sales 90D × 2<br/>Note: Returns 0 if Sales 90D is 0";
  }
  if (column === "sell_out_180_days") {
    return "Days Of Stock < 181<br/>Note: Uses growth Days Of Stock if Growth Multiplier > 0<br/>Returns ✕ if Days Of Stock < 181, ✔ if Days Of Stock ≥ 181";
  }
  if (column === "stock_arrives_on") {
    return "Shows PO number and created date for each open purchase order";
  }
  if (column === "extra_ordered") {
    return "User input field for additional order quantity<br/>Note: When entered, added to On Order and recalculates: Total Days Of Stock, Sellout Date, Reorder Deadline<br/>Works together with Growth Multiplier adjustments";
  }
  if (column === "warehouse_days_of_stock") {
    return "On Hand ÷ Daily Avg<br/>Note: Uses growth Daily Avg if Growth Multiplier > 0<br/>Does NOT include Extra Ordered quantity<br/>Returns 0 if Adjusted Daily Avg is 0 or negative";
  }
  if (column === "warehouse_sell_out_date") {
    return "Current Date + Days Of Stock (excludes On Order)<br/>Note: Uses growth Days Of Stock if Growth Multiplier > 0<br/>Only calculated if Days Of Stock > 0<br/>May show “Sold Out” (no on hand) or “No Velocity” (no sales).";
  }
  return null;
}